import React, { useState } from "react";
import { Accordion, Icon, Label, Menu, Message, Segment } from "semantic-ui-react";
import styles from "../../../../Assessment/AssessmentInfoSlideControls/LearnMoreTemplate.module.css";

const cdnRoute = process.env.REACT_APP_CDN_ROUTE + "/legal/";

export enum legalDocumentsMenuTab {
  TAB1 = `Learn More About Wills`,
  TAB2 = `Understanding Trusts`,
  TAB3 = `What is a Power Of Attorney?`, 
  TAB4 = `Power Of Attorney Options`
}

export const LegalDocumentsLearnMore: React.FC = () => {  
  const [activeLearnMoreMenuItem, setActiveLearnMoreMenuItem] = useState(legalDocumentsMenuTab.TAB1);
  const [activeAccordionIndexes] = useState([0,1,2,3,4]);

  const renderAccordionUnderstanding = () => {
    return (
      <>
      <Segment raised className={styles.segmentWhiteBG} > 
        <p className={styles.learnMoreAccordionText} >
          A will, power of attorney, and/or trust agreements should be executed while the older adult is 
          competent; otherwise, a court action is required to transfer the responsibility for management of the 
          financial affairs to another person.
          <br /><br />
        <span className={styles.learnMoreAccordionTextEmphasis} >
          Because the documents are often complex, it is recommended that older adults complete them while they 
          are healthy and able to discuss their preferences with children, family members, and other possible 
          decision makers.</span>
          <br /><br />
          After documents are completed, they should be shared with family members, physicians, and clergy to be 
          sure they are entered into the medical record in case the older adult is hospitalized. It is also 
          important to keep these documents secured in a fire-proof safe and/or a secure electronic vault.</p>
      </Segment>
      </>
    );
  };
  const displayActiveMenuSection = () => {
      switch(activeLearnMoreMenuItem) {
        case legalDocumentsMenuTab.TAB1:  
          return renderMenuTab1();
        case legalDocumentsMenuTab.TAB2:  
          return renderMenuTab2();
        case legalDocumentsMenuTab.TAB3:  
          return renderMenuTab3();
        case legalDocumentsMenuTab.TAB4:  
          return renderMenuTab4();
    }
  };
  const renderMenuTab1 = () => {
    return (
      <>
        <p className={styles.learnMoreAccordionTextEmphasis} style={{marginTop: '2rem'}}  >
          A will is a legal document that incorporates a financial inventory and states how property and assets 
          should be distributed upon death. </p>
        <p className={styles.learnMoreAccordionText}>
          If the older adult does not have a will, you should encourage them to consult an attorney. A will 
          does not override beneficiary designations stated in certain documents (insurance policies, bank accounts 
          and annuities), so it is usually better to specifically name beneficiaries for these types of accounts and 
          policies instead of simply stating that the proceeds be made payable to the older adult's estate. </p>
        <p className={styles.learnMoreAccordionText} style={{marginBottom: '3rem'}} >
          <span className={styles.learnMoreAccordionTextEmphasis}> A well-executed will reduces the likelihood of 
            family conflicts over funeral and burial decisions, as well as conflicts over the distribution of material 
            goods. </span> 
          A periodic review of an existing will is necessary when there are tax-law changes, if there 
          has been an out-of-state move, a purchase of out-of-state property, marriage, divorce, births, education 
          needs, health needs, and/or charity involvement.</p>
      </>
    );
  };
  const renderMenuTab2 = () => {
    return (
      <>
        <p className={styles.learnMoreAccordionText} style={{marginTop: '2rem'}} >
          <span className={styles.learnMoreAccordionTextEmphasis}> 
            A trust is a legal agreement that allows a third party the right to hold and direct benefits on behalf 
            of a beneficiary. </span> <br/> <br/>
          Trust agreements are particularly useful to insure uninterrupted property management, particularly if property is owned in 
          multiple states.</p>
        <p className={styles.learnMoreAccordionText} >
          A person who creates the trust is the <b>grantor</b>, the person who manages the trust is the <b>trustee</b>, 
          and the person who benefits from the trust is the <b>beneficiary</b>. In a revocable trust, the older adult 
          may serve as the grantor, trustee, and the beneficiary. An alternate trustee can take over when the older 
          adult can no longer manage the property. </p>
        <p className={styles.learnMoreAccordionText} style={{marginBottom: '3rem'}} >
          Upon death, generally without probate, the property can be distributed to one or more designated beneficiaries. 
          Once the trust is established, property must be conveyed to the trust by signing and recording deeds for real 
          estate. For bank accounts and other titled property, including insurance policies and vehicles, the assets must 
          be transferred and re-titled in the name of the trustee. </p>
      </>
    );
  };
  const renderMenuTab3 = () => {
    return (
      <>
      <p className={styles.learnMoreAccordionText} style={{marginTop: '2rem'}} >
        <span className={styles.learnMoreAccordionTextEmphasis}> A power of attorney is a legal document that gives a 
          chosen person legal authority to act on another person's behalf. </span>  <br/> <br/>
        Setting this up is a fairly simple process and is important to do while the older adult is physically and mentally 
        healthy to ensure their wishes are followed. </p>
      <p className={styles.learnMoreAccordionText} >
        It should be noted that if the older adult has signed a power of attorney and later becomes incompetent by 
        way of dementia, Alzheimer's, illness or accident, the power of attorney is automatically terminated. To avoid 
        this situation, we recommend a <b>durable power of attorney</b> which will remain in effect regardless of any 
        potential incapacity. </p>
      <p className={styles.learnMoreAccordionText} style={{marginBottom: '3rem'}} >
        A <b>springing power of attorney</b> makes the authority effective on a certain date in the future, or upon the
        occurrence of a particular event such as disability or incapacity. This is a good option if the older adult isn't
        ready to give over their rights today, but wants to be prepared for the future. </p>
      </>
    );
  };
  const renderMenuTab4 = () => {
    return (
      <>
      <p className={styles.learnMoreAccordionText} style={{marginTop: '2rem'}} >
        It is important to note that there are different types of these documents and having both will ensure the older 
        adult is fully covered in the event they become unable to make decisions on their own. </p>
      <p className={styles.learnMoreAccordionText} >
        <b>A healthcare power of attorney</b> is needed to develop advance directives for care and will appoint someone 
        to make healthcare decisions on their behalf. </p>
      <p className={styles.learnMoreAccordionText} style={{marginBottom: '3rem'}} >
        A <b>legal power of attorney</b> is needed to act on the older adult's behalf on legal and financial manners like 
        paying bills and accessing assets. </p>
      </>
    );
  };
  
  const accordions = [
    { key: 'understanding',
      index: 0,
      title: { 
        icon: <Icon name='dropdown' className={styles.learnMoreAccordionLabelTitleIcon} />, 
        content: <Label className={styles.learnMoreAccordionLabelTitle} > Understanding Legal Documents </Label> },
      content: { 
        content: (<Message className={styles.learnMoreAccordionMessageBox} content={renderAccordionUnderstanding()}/>)
      }
    }
  ];
  
  return (
    <>
      <Segment className={styles.learnMoreBackgroundImg} style = {{ backgroundImage: `url(${cdnRoute}LegalDocumentsInfoOne.png)`, }}>
        <div className={styles.learnMoreModalContent}  >

          <div className={styles.learnMoreAccordionFullDivGrid} >
            <div className={styles.learnMoreAccordionFullDiv} >
              <Accordion panels={accordions} className={styles.learnMoreModalAccordionGroup} defaultActiveIndex={activeAccordionIndexes} exclusive={false} />
            </div> 
          </div>  

          <div className={styles.learnMoreMenuDiv} >
            <Segment.Group raised className={styles.learnMoreMenuSegmentGroup} >
              <Menu attached='top' tabular color="green" className={styles.learnMoreMenuTab} widths='four'>
                <Menu.Item
                  name={legalDocumentsMenuTab.TAB1}
                  active={activeLearnMoreMenuItem === legalDocumentsMenuTab.TAB1}
                  onClick={() => setActiveLearnMoreMenuItem(legalDocumentsMenuTab.TAB1)}
                />
                <Menu.Item
                  name={legalDocumentsMenuTab.TAB2}
                  active={activeLearnMoreMenuItem === legalDocumentsMenuTab.TAB2}
                  onClick={() => setActiveLearnMoreMenuItem(legalDocumentsMenuTab.TAB2)}
                />
                <Menu.Item
                  name={legalDocumentsMenuTab.TAB3}
                  active={activeLearnMoreMenuItem === legalDocumentsMenuTab.TAB3}
                  onClick={() => setActiveLearnMoreMenuItem(legalDocumentsMenuTab.TAB3)}
                />
                <Menu.Item
                  name={legalDocumentsMenuTab.TAB4}
                  active={activeLearnMoreMenuItem === legalDocumentsMenuTab.TAB4}
                  onClick={() => setActiveLearnMoreMenuItem(legalDocumentsMenuTab.TAB4)}
                />
              </Menu>
              <Segment attached='bottom' raised className={styles.learnMoreMenuTabbedSegment} >
                {displayActiveMenuSection()}
              </Segment>
            </Segment.Group>
          </div> 
    
        </div>
      </Segment>    
    </>
    );  
};
